import React, { useState, useEffect } from 'react';
import { Menu, X, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { projectInfo, contactInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Overview', href: '#overview' },
    { label: 'Site Plan', href: '#siteplan' },
    { label: 'Tenant Mix', href: '#tenants' },
    { label: 'Location', href: '#location' },
    { label: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Top Bar */}
      <div className={`hidden md:block transition-all duration-300 ${isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'}`}>
        <div className="bg-slate-900 text-white py-2">
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2">
                <MapPin size={14} className="text-emerald-400" />
                {projectInfo.address}, {projectInfo.city}, {projectInfo.state}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 hover:text-emerald-400 transition-colors">
                <Phone size={14} />
                {contactInfo.phone}
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 hover:text-emerald-400 transition-colors">
                <Mail size={14} />
                {contactInfo.email}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg transition-colors ${
              isScrolled ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-600'
            }`}>
              SP
            </div>
            <div>
              <h1 className={`font-bold text-lg leading-tight transition-colors ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
                Stadium Plaza
              </h1>
              <p className={`text-xs transition-colors ${isScrolled ? 'text-slate-500' : 'text-white/70'}`}>Center</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollToSection(link.href)}
                className={`text-sm font-medium transition-colors hover:text-emerald-500 ${
                  isScrolled ? 'text-slate-700' : 'text-white/90'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
            >
              Leasing Inquiry
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-slate-900 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t">
            <div className="p-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left py-2 text-slate-700 hover:text-emerald-600 font-medium"
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('#contact')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-4"
              >
                Leasing Inquiry
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
